import { Add } from "@mui/icons-material";
import { Box, Button, LinearProgress, Stack, Typography } from "@mui/material";
import React from "react";

import SortedDataGridPro6 from "../../../../design/sorted-datagrid-pro-6";
import useBusStopColumns from "../../controllers/use-bus-stop-columns";
import useTransportData from "../../controllers/use-transport-data";
import { BusStop } from "../../store/transport-types";

interface BusStopTableProps {
	locationId: number;
	handleOpenAdd: () => void;
	handleOpenEdit: (row: BusStop) => void;
}

const BusStopTable = ({
	locationId,
	handleOpenAdd,
	handleOpenEdit,
}: BusStopTableProps) => {
	const {
		busStops,
		busStopsLocationId,
		getBusStopsStatus,
		getBusStopsErrorMessage,
		updateBusStopStatusStatus,
	} = useTransportData();
	const { columns } = useBusStopColumns({ locationId, handleOpenEdit });

	// The slice holds one list at a time, so a list still belonging to a
	// previously opened location must not be shown against this one.
	const rows = busStopsLocationId === locationId ? busStops : [];

	return (
		<Box
			sx={{
				border: "1px solid var(--divider, rgba(0, 0, 0, 0.12))",
				borderRadius: "8px",
				backgroundColor: "#fff",
				padding: "20px",
				marginTop: "16px",
			}}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "row",
					alignItems: "center",
					justifyContent: "space-between",
					paddingTop: "20px",
					paddingBottom: "20px",
				}}
			>
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontWeight: 400,
						fontSize: "16px",
						lineHeight: "24px",
						color: "#000000DE",
					}}
				>
					Stops
				</Typography>
				<Button
					disableElevation
					sx={{ height: 36 }}
					variant='outlined'
					color='primary'
					onClick={handleOpenAdd}
					startIcon={<Add />}
				>
					Stop
				</Button>
			</Box>
			<Box sx={{ padding: "12px 8px" }}>
				<SortedDataGridPro6
					columns={columns}
					rows={rows}
					getRowId={(row: any) => row.id}
					defaultSortField='name'
					disableColumnSelector
					disableDensitySelector
					disableColumnMenu
					pagination
					initialState={{
						pagination: { paginationModel: { pageSize: 10 } },
						filter: {},
					}}
					pageSizeOptions={[10, 25, 50]}
					autoHeight
					disableRowSelectionOnClick
					loading={
						getBusStopsStatus === "pending" ||
						updateBusStopStatusStatus === "pending"
					}
					slots={{
						loadingOverlay: () => <LinearProgress />,
						noRowsOverlay: () => (
							<Stack
								height='100%'
								alignItems='center'
								justifyContent='center'
								sx={{ padding: "24px" }}
							>
								<Typography
									sx={{
										fontFamily: "Roboto",
										fontSize: "14px",
										fontWeight: 400,
										color: "rgba(0, 0, 0, 0.38)",
										textAlign: "center",
									}}
								>
									{getBusStopsStatus === "error"
										? `Error: ${getBusStopsErrorMessage}`
										: "No stops added. Workers cannot book transport at this location until at least one active stop exists."}
								</Typography>
							</Stack>
						),
					}}
					sx={{
						border: "none !important",
						".MuiDataGrid-cell": {
							"&:focus": { outline: "none" },
							"&.hover-column-cell": {
								position: "sticky",
								right: 0,
								zIndex: 1,
							},
							"& .hover-content": {
								transition: "opacity 0.3s ease-in-out",
								opacity: 0,
								backgroundColor: "transparent",
							},
						},
						".MuiDataGrid-cell:focus-within": { outline: "none" },
						"& .MuiDataGrid-columnHeader:focus-within": { outline: "none" },
						".MuiDataGrid-row:hover .hover-column-cell .hover-content": {
							opacity: 1,
							backgroundColor: "transparent",
						},
					}}
				/>
			</Box>
		</Box>
	);
};

export default BusStopTable;
